import { FC, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import DoctorProfile from '../components/DoctorProfile';
import { Provider, Speciality } from '../typings';
import { MyState } from '../store';
import ProfileInformationList from './edit-profile/ProfileInformationList';
import { Creators as ProviderActions } from '../modules/provider.module';
import ProfilePriceItem from './edit-profile/ProfilePriceItem';
import MMDModal from '../components/MMDModal';
import TextProfileInfo from './edit-profile/TextProfileInfo';
import { PSYCHOLOGY } from '../utils/constants';
import { FilesPage } from './edit-profile/FilesPage';
import { selectProviderFilesState } from '../modules/provider-files.module';
import { ProviderFile } from '../typings';

type FilesWarningType = 'missing' | 'pending';

const hasMissingRequiredFiles = (files: ProviderFile[]) =>
  files.some((category) => category.required && category.files.length === 0);

const hasPendingFiles = (files: ProviderFile[]) =>
  files.some((category) =>
    category.files.some((file) => file.status === 'pending'),
  );

type Props = {
  provider: Provider;
} & typeof ProviderActions;

const EditProfile: FC<Props> = ({
  provider,
  requestUpdateProviderProfile,
  requestCustomPrice,
}) => {
  const [pricePage, setPricePage] = useState(false);
  const [filesPage, setFilesPage] = useState(false);
  const [showModalPlan, setShowModalPlan] = useState(false);
  const [showFilesWarning, setShowFilesWarning] = useState(false);
  const [filesWarningType, setFilesWarningType] =
    useState<FilesWarningType>('missing');
  const [textRequestPlan, setTextRequestPlan] = useState<string | undefined>();
  const [planType, setPlanType] = useState<string | undefined>();
  const files = useSelector(selectProviderFilesState);
  const showFilesPage = filesPage && !provider.teacher;

  useEffect(() => {
    if (provider.teacher || !provider.documentUploadNeeded) {
      return;
    }

    if (hasMissingRequiredFiles(files)) {
      setFilesWarningType('missing');
      setShowFilesWarning(true);
      setFilesPage(true);
      return;
    }

    if (hasPendingFiles(files)) {
      setFilesWarningType('pending');
      setShowFilesWarning(true);
    }
  }, [provider.documentUploadNeeded, provider.teacher, files]);

  const specialty: Speciality | object = provider.profile
    ? provider.profile.specialities && provider.profile.specialities.length
      ? provider.profile.specialities[0]
      : {}
    : {};

  return (
    <Container fluid>
      <Row>
        <Col xs={12} className="text-center align-middle mb-3 mt-5">
          <h1>My Profile</h1>
        </Col>
      </Row>
      <Row className="justify-content-center align-middle pt-1 pb-1">
        <Col xs={12} sm={12} md={12} lg={3}>
          <DoctorProfile editable />
        </Col>
        <Col xs={12} sm={12} md={12} lg={9}>
          {!pricePage && !showFilesPage && (
            <ProfileInformationList
              files={files}
              provider={provider.id ? provider : null}
              requestUpdateProviderProfile={requestUpdateProviderProfile}
              setPricePage={() => setPricePage(true)}
              setFilesPage={() => setFilesPage(true)}
            />
          )}
          {pricePage && (
            <div>
              <ProfilePriceItem
                id={1}
                title="Video Consultations"
                description={`${
                  (specialty as Speciality).timeSlotLength
                } minute consultation with a patient`}
                type="oneTime"
                includes={[
                  'notes',
                  'e-prescribing',
                  'file sharing',
                  'one week of messaging',
                ]}
                price={provider.oneTimePrice}
                onClick={() => {
                  setPlanType('oneTime');
                  setShowModalPlan(true);
                }}
              />
              {provider.isPsychiatrist ? (
                <ProfilePriceItem
                  id={2}
                  title={`${
                    (specialty as Speciality).doctorType === PSYCHOLOGY
                      ? 'Extended time consultation'
                      : 'First time consultation'
                  } (applicable only to psychiatric specialties)`}
                  description={`${
                    (specialty as Speciality).firstTimeLength
                  } minute consultation with a patient`}
                  type="firstTime"
                  includes={[
                    'notes',
                    'e-prescribing',
                    'file sharing',
                    'one week of messaging',
                  ]}
                  price={provider.firstTimePrice}
                  onClick={() => {
                    setPlanType('firstTime');
                    setShowModalPlan(true);
                  }}
                />
              ) : null}
              <ProfilePriceItem
                id={provider.isPsychiatrist ? 3 : 2}
                title="Monthly Subscription"
                description="Unlimited video consultations"
                type="monthly"
                includes={[
                  'messaging',
                  'e-prescribing',
                  'includes notes',
                  'file sharing',
                ]}
                price={provider.monthlyPrice}
                onClick={() => {
                  setPlanType('monthly');
                  setShowModalPlan(true);
                }}
              />
              <Button
                variant="outline-dark"
                className="height-30 margin-top-35 padding-h-50"
                onClick={() => setPricePage(false)}
              >
                <h6>Back</h6>
              </Button>
            </div>
          )}
          {showFilesPage && (
            <FilesPage onGoBack={() => setFilesPage(false)} />
          )}
          <MMDModal
            show={showModalPlan}
            classNameContainer={'modal-container-padding'}
            title="Payment Plan Request"
            titleClassName="payment-plan-pequest"
            handleClose={() => setShowModalPlan(false)}
            renderBody={() => (
              <TextProfileInfo
                textAreaClassName={'border-solid'}
                text={textRequestPlan}
                setText={setTextRequestPlan}
              />
            )}
            renderFooter={() => (
              <Row className="w-100 center-footer">
                <Col xs={5} className="text-center modal-footer-btn">
                  <Button
                    className="w-100 primary height-30"
                    onClick={() => {
                      requestCustomPrice({
                        description: textRequestPlan,
                        planType: planType,
                      });
                      setShowModalPlan(false);
                      setPlanType('');
                      setTextRequestPlan('');
                    }}
                  >
                    <h6>Send request</h6>
                  </Button>
                </Col>
              </Row>
            )}
          />
          <MMDModal
            show={showFilesWarning}
            centered
            title={
              filesWarningType === 'pending'
                ? 'Files Pending Review'
                : 'Required Files Missing'
            }
            handleClose={() => setShowFilesWarning(false)}
            renderBody={() => (
              <p>
                {filesWarningType === 'pending'
                  ? 'All required files have been uploaded. Some of your files are still pending review. You will be able to use the platform once they are approved.'
                  : 'You have required files that need to be uploaded. Please upload all required files marked with an asterisk (*) to continue using the platform.'}
              </p>
            )}
            renderFooter={() => (
              <Button
                variant="primary"
                onClick={() => setShowFilesWarning(false)}
              >
                OK
              </Button>
            )}
          />
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state: MyState) => ({
  provider: state.provider,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ ...ProviderActions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(EditProfile);
